import React from 'react'
import { IconTrash } from '@tabler/icons-react'
import { isEmpty, isString } from 'lodash'

import { Button } from '@/components/buttons/Button'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import Select from '@/components/forms/Select'
import { TextInput } from '@/components/forms/TextInput'
import { FormField } from '@/components/forms/FormField'

import { PATH_PLACEHOLDER } from './paths'

import './SourceRow.scss'

/**
 * One value source: an optional source dropdown plus the path into that payload.
 *
 * The source select is optional rather than forked. TCN reads from four different
 * payloads, so it needs the prefix; a connector with a single webhook body does not,
 * and giving it its own copy of this row is how two implementations start drifting.
 *
 * Choosing a whole-value source (TCN's Call Direction, Hunt Group) disables the path,
 * because neither is addressable as one.
 */
export const SourceInputs = ({
  sources,
  examples = {},
  isWholeValue = () => false,
  placeholder = PATH_PLACEHOLDER,
  sourceSpan = 5,
  pathSpan = 7,
  // What the path column is actually called. GenericEventValuesFields passes
  // "Value" -- those rows hold literal vendor event names, not paths, which is why
  // that field uses plainValueCodec. Without this the header reads "Value" while a
  // screen reader announces "Path 1".
  pathLabel = 'Path',
  prefix,
  path,
  onChange,
  testIdPrefix,
  index,
}) => {
  const hasSources = !isEmpty(sources)
  const wholeValue = isWholeValue(prefix)

  const pathPlaceholder = () => {
    if (wholeValue) {
      return 'No path — this source is the whole value'
    }
    if (!hasSources) {
      return placeholder
    }
    return examples[prefix] || 'Choose a source first'
  }

  return (
    <>
      {hasSources && (
        <FormField span={sourceSpan}>
          {/*
           * The shared Select, not react-select directly: web/CLAUDE.md routes every
           * view through it, and it carries the styles, theme and menu portalling
           * that keep this dropdown consistent with the rest of the app. It resolves
           * a scalar value against the options -- flattening groups itself -- so the
           * stored prefix is passed straight in.
           */}
          <Select
            options={sources}
            value={prefix}
            onChange={(option) => onChange({ prefix: option ? option.value : '', path })}
            placeholder="Source…"
            inputId={`${testIdPrefix}-prefix-${index}`}
            aria-label={`Source ${index + 1}`}
            classNamePrefix={`${testIdPrefix}-prefix`}
          />
        </FormField>
      )}
      {/*
       * Without a source select the path takes the width that select would have
       * used, so the trash button stays in the same column either way.
       */}
      <FormField span={hasSources ? pathSpan : sourceSpan + pathSpan}>
        <TextInput
          disabled={wholeValue}
          value={wholeValue ? '' : path}
          placeholder={pathPlaceholder()}
          data-testid={`${testIdPrefix}-path-${index}`}
          aria-label={`${pathLabel} ${index + 1}`}
          onChange={(event) => onChange({ prefix, path: event.target.value })}
          // Trim on blur, not on change: a predicate value contains spaces
          // ("rows[fieldLabel=NEARSHORE CONS].fieldValue"), and trimming per
          // keystroke would eat the space the moment it is typed. Stray outer
          // whitespace resolves to nothing server-side, so it is cleaned before
          // it can be saved.
          onBlur={(event) => {
            const trimmed = event.target.value.trim()
            if (trimmed !== event.target.value) {
              onChange({ prefix, path: trimmed })
            }
          }}
        />
      </FormField>
    </>
  )
}

/** The trash button every row ends with. */
export const RemoveRowButton = ({ onClick, label, testId }) => (
  <FormField>
    <Button type="button" square critical aria-label={label} data-testid={testId} onClick={onClick}>
      <IconTrash />
    </Button>
  </FormField>
)

/** Array-level Yup errors arrive as a string; per-row ones are rendered per row. */
export const ArrayLevelError = ({ error }) =>
  isString(error) ? <ErrorMessage content={error} /> : null

/**
 * One row's validation message, shown against the row that caused it.
 *
 * Deliberately NOT gated on `touched`: these fields are written with
 * setFieldValue, which never marks anything touched, and a stored config is
 * validated on mount. Gating would leave the submit button disabled with nothing
 * on screen explaining why -- which is exactly what it did.
 */
export const RowError = ({ message }) =>
  isString(message) ? <ErrorMessage content={message} /> : null

/**
 * A reference block of worked examples, so every field that takes a path documents
 * the same vocabulary once.
 *
 * `examples` is a list of { term, definition }; term is rendered as-is so a caller
 * can show either "Client Info → path" or a bare path.
 */
export const PathExamplesHelp = ({ intro, examples = [], outro }) => (
  <div className="form-caption path-source-help">
    {intro && <div>{intro}</div>}
    <dl>
      {examples.map(({ term, definition }) => (
        <React.Fragment key={definition}>
          <dt>{term}</dt>
          <dd>{definition}</dd>
        </React.Fragment>
      ))}
    </dl>
    {outro && <div>{outro}</div>}
  </div>
)
